import React, { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

function Stars({ value, size = 16 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} fill={i <= Math.round(value) ? "#D8B86A" : "none"} stroke="#D8B86A" />
      ))}
    </div>
  );
}

const GoogleWord = () => (
  <span className="font-bold tracking-tight" aria-label="Google">
    <span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span><span className="text-[#FBBC05]">o</span>
    <span className="text-[#4285F4]">g</span><span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span>
  </span>
);

function ReviewCard({ r }) {
  const [open, setOpen] = useState(false);
  const long = r.text.length > 260;
  return (
    <div className="bg-white p-6 sm:p-7 border border-gray-200 rounded-sm flex flex-col" data-testid="google-review-card">
      <Stars value={r.rating} />
      <p className="text-[#111111] leading-relaxed my-4 text-sm flex-1">
        "{long && !open ? `${r.text.slice(0, 260).trim()}…` : r.text}"
        {long && <button onClick={() => setOpen(!open)} className="ml-1 text-[#2877C2] font-bold text-xs">{open ? "Moins" : "Lire la suite"}</button>}
      </p>
      <div className="pt-4 border-t border-gray-200 flex items-center gap-3">
        {r.photo ? <img src={r.photo} alt="" referrerPolicy="no-referrer" loading="lazy" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-[#072135] text-[#D8B86A] flex items-center justify-center font-bold">{r.author[0]}</div>}
        <div className="min-w-0">
          {r.author_uri ? (
            <a href={r.author_uri} target="_blank" rel="noopener noreferrer nofollow" className="font-heading font-bold text-[#072135] hover:underline block truncate">{r.author}</a>
          ) : <div className="font-heading font-bold text-[#072135] truncate">{r.author}</div>}
          <div className="text-xs text-[#4B5563] font-mono-tech">{r.date} · avis <GoogleWord /></div>
        </div>
      </div>
    </div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reviews/google").then((r) => setData(r.data)).catch(() => setData({ available: false })); }, []);
  if (!data?.available || !data.reviews?.length) return null;

  return (
    <section className="lmb-section bg-[#F9F9F9]" data-testid="avis-section">
      <div className="lmb-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 sm:mb-14">
          <div>
            <div className="text-xs font-mono-tech tracking-[0.2em] text-[#D8B86A] uppercase mb-2">— Avis clients Google</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#072135]">
              Ce qu'ils <span className="text-[#2877C2]">disent de nous</span>
            </h2>
          </div>
          <a href={data.maps_uri} target="_blank" rel="noopener noreferrer" data-testid="google-rating-badge"
            className="inline-flex items-center gap-4 bg-white border border-gray-200 rounded-sm px-5 py-3 hover:border-[#D8B86A] transition-colors self-start md:self-auto">
            <div className="text-4xl font-heading font-black text-[#072135] leading-none" data-testid="google-rating-value">{data.rating?.toFixed(1).replace(".", ",")}</div>
            <div>
              <Stars value={data.rating} size={15} />
              <div className="text-xs text-[#4B5563] mt-1"><span data-testid="google-rating-count">{data.count}</span> avis sur <GoogleWord /></div>
            </div>
            <ExternalLink size={14} className="text-[#4B5563]" />
          </a>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {data.reviews.slice(0, 6).map((r) => <ReviewCard key={`${r.author}-${r.publish_time}`} r={r} />)}
        </div>
        <div className="text-center mt-8">
          <a href={data.maps_uri} target="_blank" rel="noopener noreferrer" className="lmb-btn-secondary text-xs" data-testid="google-reviews-all">
            Voir tous les avis sur Google <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </section>
  );
}
